import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  FiberNew as NewIcon,
  Schedule as ProgressIcon,
  PendingActions as PendingIcon,
  Refresh as RedoIcon,
  CheckCircle as ApprovedIcon,
  Archive as ArchiveIcon,
  Dashboard as DefaultIcon
} from '@mui/icons-material';
import { Box, Grid } from '@mui/material';
import styled from 'styled-components';
import './ProgressCards.css';
import { apiCall } from "../../services/ApiCall.js";
import config from "../../config/config.json";

// Enhanced styled components for animations
const StyledProgressCard = styled.div`
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  height: 100%;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 30px rgba(13, 110, 253, 0.15);
    
    .progress-card-icon-container {
      transform: scale(1.1);
      box-shadow: 0 8px 25px rgba(13, 110, 253, 0.2);
    }
    
    .progress-card-value {
      transform: scale(1.05);
      color: #0d6efd;
    }
    
    .change-icon {
      animation: bounce 0.6s ease-in-out;
    }
  }
  
  @keyframes bounce {
    0%, 20%, 60%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-4px);
    }
    80% {
      transform: translateY(-2px);
    }
  }
`;

const AnimatedIconContainer = styled.div`
  border-radius: 50%;
  padding: 12px;
  margin-right: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 50px;
  height: 50px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    padding: 2px;
    -webkit-mask-composite: xor;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
`;

const ResponsiveContainer = styled.div`
  /* Desktop: 4 cards per row */
  @media (min-width: 1200px) {
    .progress-cards-grid {
      grid-template-columns: repeat(4, 1fr);
    }
  }
  
  /* Tablet: 3 cards per row */
  @media (min-width: 768px) and (max-width: 1199px) {
    .progress-cards-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }
  
  /* Mobile: 2 cards per row */
  @media (max-width: 767px) {
    .progress-cards-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
    }
    
    .progress-card {
      padding: 16px;
      min-height: 140px;
    }
    
    .progress-card-title {
      font-size: 12px;
      line-height: 1.3;
      margin-bottom: 8px;
    }
    
    .progress-card-value {
      font-size: 20px;
      margin-bottom: 6px;
    }
    
    .progress-card-change {
      font-size: 11px;
    }
    
    .progress-card-icon-container {
      width: 40px;
      height: 40px;
      padding: 8px;
    }
    
    .progress-card-icon-container img {
      width: 20px !important;
      height: 20px !important;
    }
    
    .menu-dots {
      font-size: 16px;
    }
  }
`;

const ProgressCard = ({ icon, title, value, bgIconColor }) => (
  <StyledProgressCard className="progress-card">
    <div className="progress-card-header">
      <div className="">
        <AnimatedIconContainer className="progress-card-icon-container" style={{ backgroundColor: bgIconColor }}>
          <img src={icon} alt={title} style={{ width: 24, height: 24 }} />
        </AnimatedIconContainer>
      </div>
      <div className="progress-card-menu">
      </div>
    </div>
    
    <div className="progress-card-content">
      <div className="progress-card-title">{title}</div>
      <div className="progress-card-value">{value}</div>
      
      <div className={`progress-card-change positive`}>
        <span className="change-icon">📈</span>
        <span className="change-text">12%</span>
      </div>
    </div>
    <span className="menu-dots">⋯</span>
  </StyledProgressCard>
);

const ProgressCards = () => {
  const navigate = useNavigate();
  const [requestData, setRequestData] = useState({});

  const getRequestData = async () => {
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getRequestGraphData`,
      {},
      {},
      "GET"
    );

    if (isSuccess) {
      const requestDatas = data?.data.reduce((acc, request) => {
        const { status, id, createdAt } = request;
        if (!acc[status]) {
          acc[status] = [];
        }
        acc[status].push({ id, createdAt });
        return acc;
      }, {});
      setRequestData(requestDatas);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    }
    getRequestData();
  }, []);

  const cards = [
    { 
      icon:'/img/new-request.png',
      title: 'New Requests', 
      value: requestData && requestData[0] ? requestData[0].length : 0, 
      link: '/new-Request',
      bgIconColor: '#E4F3FF'
    },
    { 
      icon: '/img/in-progress.png',
      title: 'In-Progress Request', 
      value: requestData && requestData[1] ? requestData[1].length : 0, 
      link: '/in-progress',
      bgIconColor: '#FFEDD5'
    },
    { 
      icon: '/img/to-be-approved.png',
      title: 'To-Be-Approved Requests', 
      value: requestData && requestData[2] ? requestData[2].length : 0,
      link: '/to-be-approved',
      bgIconColor: '#FFEBF1'
    },
    { 
      icon: '/img/redo.png',
      title: 'Redo Requests', 
      value: requestData && requestData[7] ? requestData[7].length : 0,
      link: '/redo-request',
      bgIconColor: '#FEF9C3'
    },
    { 
      icon: '/img/approved.png',
      title: 'Approved Requests', 
      value: requestData && requestData[4] ? requestData[4].length : 0,
      link: '/approved-Request',
      bgIconColor: '#DCFCE7'
    },
    { 
      icon: '/img/archive.png',
      title: 'Archive Requests', 
      value: requestData && requestData[9] ? requestData[9].length : 0,
      link: '/archive-request',
      bgIconColor: '#F3F4F6'
    },
    {
      icon:'/img/expired.png', 
      title: 'Expired Requests', 
      value: requestData && requestData[10] ? requestData[10].length : 0,
      link: '/expired-request',
      bgIconColor: '#F3E8FF'
    }
  ];

  return (
    <ResponsiveContainer>
      {/* Material-UI Grid System - Alternative Approach */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <div className="progress-cards-container">
          {cards.map((card, index) => (
            card.link ? (
              <NavLink key={index} style={{ textDecoration: 'none' }} to={card.link}>
                <ProgressCard {...card} />
              </NavLink>
            ) : (
              <ProgressCard key={index} {...card} />
            )
          ))}
        </div>
      </Box>

      {/* Mobile Grid Layout */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, px: 1 }}>
        <Grid container spacing={1.5}>
          {cards.map((card, index) => (
            <Grid item xs={6} key={index}>
              {card.link ? (
                <NavLink style={{ textDecoration: 'none' }} to={card.link}>
                  <StyledProgressCard 
                    className="progress-card"
                    style={{
                      padding: '12px',
                      minHeight: '120px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      position: 'relative'
                    }}
                  >
                    <div className="progress-card-header" style={{ marginBottom: '8px' }}>
                      <div className="">
                        <AnimatedIconContainer 
                          className="progress-card-icon-container" 
                          style={{ 
                            backgroundColor: card.bgIconColor,
                            width: 35,
                            height: 35,
                            padding: 6,
                            marginBottom: 0
                          }}
                        >
                          <img src={card.icon} alt={card.title} style={{ width: 18, height: 18 }} />
                        </AnimatedIconContainer>
                      </div>
                    </div>
                    
                    <div className="progress-card-content" style={{ flex: 1 }}>
                      <div className="progress-card-title" style={{ 
                        fontSize: '11px', 
                        lineHeight: 1.2,
                        marginBottom: '4px',
                        fontWeight: '500',
                        color: '#666',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {card.title}
                      </div>
                      <div className="progress-card-value" style={{ 
                        fontSize: '18px',
                        marginBottom: '4px',
                        fontWeight: '600'
                      }}>
                        {card.value}
                      </div>
                      
                      <div className="progress-card-change positive" style={{ 
                        fontSize: '9px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        <span className="change-icon" style={{ fontSize: '10px' }}>📈</span>
                        <span className="change-text">12%</span>
                      </div>
                    </div>
                    <span className="menu-dots" style={{ 
                      fontSize: '14px',
                      position: 'absolute',
                      top: '8px',
                      right: '8px'
                    }}>⋯</span>
                  </StyledProgressCard>
                </NavLink>
              ) : (
                <StyledProgressCard 
                  className="progress-card"
                  style={{
                    padding: '12px',
                    minHeight: '120px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative'
                  }}
                >
                  <div className="progress-card-header" style={{ marginBottom: '8px' }}>
                    <div className="">
                      <AnimatedIconContainer 
                        className="progress-card-icon-container" 
                        style={{ 
                          backgroundColor: card.bgIconColor,
                          width: 35,
                          height: 35,
                          padding: 6,
                          marginBottom: 0
                        }}
                      >
                        <img src={card.icon} alt={card.title} style={{ width: 18, height: 18 }} />
                      </AnimatedIconContainer>
                    </div>
                  </div>
                  
                  <div className="progress-card-content" style={{ flex: 1 }}>
                    <div className="progress-card-title" style={{ 
                      fontSize: '11px', 
                      lineHeight: 1.2,
                      marginBottom: '4px',
                      fontWeight: '500',
                      color: '#666',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {card.title}
                    </div>
                    <div className="progress-card-value" style={{ 
                      fontSize: '18px',
                      marginBottom: '4px',
                      fontWeight: '600'
                    }}>
                      {card.value}
                    </div>
                    
                    <div className="progress-card-change positive" style={{ 
                      fontSize: '9px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px'
                    }}>
                      <span className="change-icon" style={{ fontSize: '10px' }}>📈</span>
                      <span className="change-text">12%</span>
                    </div>
                  </div>
                  <span className="menu-dots" style={{ 
                    fontSize: '14px',
                    position: 'absolute',
                    top: '8px',
                    right: '8px'
                  }}>⋯</span>
                </StyledProgressCard>
              )}
            </Grid>
          ))}
        </Grid>
      </Box>
    </ResponsiveContainer>
  );
};

export default ProgressCards;